/**
 * A provider's working days, as the API stores them and as the editor edits
 * them. The API shape is { monday: {rule: "weekly"}, ... } with three rule
 * kinds; the editor holds one draft line per weekday so an "off" day is a
 * choice rather than an absent key. A rule may also carry `hours`, a list of
 * {start, end} "HH:MM" windows worked within the day; no hours means the
 * whole open day, which the draft holds as an empty list.
 */
export const WEEKDAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

export const RULE_KINDS = [
  { value: "off", label: "Off" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every other week" },
  { value: "nth_weekday", label: "Nth of the month" },
];

const ORDINALS = ["", "1st", "2nd", "3rd", "4th", "5th"];

const emptyLine = () => ({ kind: "off", anchor: "", n: "1", hours: [] });

/** The hours windows of a stored rule as draft windows (missing = all day). */
function hoursFromRule(rule) {
  if (!Array.isArray(rule?.hours)) return [];
  return rule.hours.map((w) => ({ start: w?.start || "", end: w?.end || "" }));
}

const TIME = /^\d{2}:\d{2}$/;

/** Windows in start order: "HH:MM" strings compare correctly as text. */
const sortedHours = (hours) => [...hours].sort((a, b) => a.start.localeCompare(b.start));

/** Editor lines for every weekday from an API map (null/undefined = all off). */
export function draftFromWeekdays(weekdays) {
  const draft = {};
  WEEKDAYS.forEach(({ key }) => {
    const rule = weekdays?.[key];
    if (!rule) draft[key] = emptyLine();
    else if (rule.rule === "biweekly") {
      draft[key] = { kind: "biweekly", anchor: rule.anchor || "", n: "1", hours: hoursFromRule(rule) };
    } else if (rule.rule === "nth_weekday") {
      draft[key] = { kind: "nth_weekday", anchor: "", n: String(rule.n ?? 1), hours: hoursFromRule(rule) };
    } else draft[key] = { kind: "weekly", anchor: "", n: "1", hours: hoursFromRule(rule) };
  });
  return draft;
}

/**
 * The API map for a draft: off days are left out, and so is `hours` on a
 * day worked whole. Windows go in start order, which the server requires
 * and the admin should not have to think about.
 */
export function weekdaysFromDraft(draft) {
  const out = {};
  WEEKDAYS.forEach(({ key }) => {
    const line = draft[key];
    if (!line || line.kind === "off") return;
    if (line.kind === "weekly") out[key] = { rule: "weekly" };
    else if (line.kind === "biweekly") out[key] = { rule: "biweekly", anchor: line.anchor };
    else if (line.kind === "nth_weekday") out[key] = { rule: "nth_weekday", n: Number(line.n) };
    else return;
    if (line.hours?.length) {
      out[key].hours = sortedHours(line.hours).map(({ start, end }) => ({ start, end }));
    }
  });
  return out;
}

function weekdayOf(isoDate) {
  // Parsed as local parts, not via Date(string), which reads "YYYY-MM-DD" as UTC.
  const [y, m, d] = isoDate.split("-").map(Number);
  const js = new Date(y, m - 1, d).getDay();          // 0 = Sunday
  return WEEKDAYS[(js + 6) % 7];
}

/** The complaint about one weekday's hours windows, or null. */
function hoursProblem(label, hours) {
  if (!hours?.length) return null;
  if (hours.some((w) => !TIME.test(w.start || "") || !TIME.test(w.end || ""))) {
    return `${label}: each hours window needs a start and an end time.`;
  }
  const backwards = hours.find((w) => w.end <= w.start);
  if (backwards) return `${label}: ${backwards.start}\u2013${backwards.end} ends before it starts.`;
  const ordered = sortedHours(hours);
  for (let i = 1; i < ordered.length; i += 1) {
    if (ordered[i].start < ordered[i - 1].end) return `${label}: hours overlap.`;
  }
  return null;
}

/**
 * What the server would refuse, in the editor's words, one line per weekday.
 * Hours on an off day are never sent, so they are not checked either.
 */
export function draftProblems(draft) {
  const problems = [];
  WEEKDAYS.forEach(({ key, label }) => {
    const line = draft[key];
    if (!line || line.kind === "off") return;
    if (line.kind === "biweekly") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(line.anchor || "")) {
        problems.push(`${label}: every other week needs a start date.`);
      } else {
        const actual = weekdayOf(line.anchor);
        if (actual.key !== key) problems.push(`${label}: ${line.anchor} is a ${actual.label}.`);
      }
    }
    const hours = hoursProblem(label, line.hours);
    if (hours) problems.push(hours);
  });
  return problems;
}

function shortDate(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  return `${d} ${month} ${y}`;
}

/** "09:00–13:00 and 14:00–17:00", or "" for a day worked whole. */
export function describeHours(hours) {
  if (!Array.isArray(hours) || hours.length === 0) return "";
  return hours.map((w) => `${w.start}\u2013${w.end}`).join(" and ");
}

/** A rule in words, for the read-only "learned" hint and the row summary. */
export function describeRule(rule) {
  if (!rule) return "off";
  let words;
  if (rule.rule === "weekly") words = "every week";
  // A stored biweekly rule always carries an anchor, but a half-typed draft
  // and an older learned guess may not, and a missing one must not take the
  // whole hint down with it.
  else if (rule.rule === "biweekly") {
    words = rule.anchor ? `every other week from ${shortDate(rule.anchor)}` : "every other week";
  } else if (rule.rule === "nth_weekday") words = `${ORDINALS[rule.n] || rule.n} of the month`;
  else return "off";
  const hours = describeHours(rule.hours);
  return hours ? `${words}, ${hours}` : words;
}

/**
 * The whole rota on one line, for the collapsed row: "Mon every week · Wed
 * every other week from 30 Sep 2026". Empty for no rota at all (null or
 * undefined); a rota that was learned or set but landed on no days at all
 * says so, rather than looking the same as never having asked.
 */
export function summariseWeekdays(weekdays) {
  if (!weekdays) return "";
  if (Object.keys(weekdays).length === 0) return "No working days";
  return WEEKDAYS
    .filter(({ key }) => weekdays[key])
    .map(({ key, label }) => `${label.slice(0, 3)} ${describeRule(weekdays[key])}`)
    .join(" · ");
}
